import { useCallback, useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import type { SortingState } from "@racoon/ui";
import {
  pickupLocationApi,
  useLazyListPickupLocationsQuery,
  useListPickupLocationsQuery,
} from "~/shared/api/pickup-locations/pickup-locations.grpc";
import {
  useLazyListPickupLocationGroupsQuery,
  useListPickupLocationGroupsQuery,
} from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { useLazyListTrucksQuery, useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { useLazyListContainerGroupSchedulesQuery } from "~/shared/api/container-group-schedule/container-group-schedule.grpc";
import {
  fetchLocationSchedules,
  useLocationSchedules,
} from "~/shared/api/container-group-schedule/use-location-schedules";
import { useAccumulatedList, withPage } from "~/shared/hooks/use-accumulated-list";
import { useActiveChildOrgs } from "~/shared/api/organization/use-active-child-orgs";
import { buildCgRouteIndex, flattenLocationsForExport } from "../lib/cascade-rows";
import {
  type LocationCriteria,
  matchesLocalCriteria,
  needsLocalFiltering,
} from "../lib/location-criteria";
import {
  locationListRequest,
  LOCATION_SORT_FIELDS,
  locationTabCounts,
  newestFirst,
  providersKey,
  withStatusFacetOnFirstPage,
} from "../lib/location-list";
import { toListSort } from "~/shared/lib/list-sort";

const PAGE_SIZE = 100;
const NO_LOCATIONS: ConnectProto.PickupLocation[] = [];

const selectRows = (data: ConnectProto.PickupLocationList) => data.pickupLocations;
const selectTotal = (data: ConnectProto.PickupLocationList) => data.listInfo?.totalItems;
const selectRowKey = (location: ConnectProto.PickupLocation) => String(location.id);

type ListArg = Parameters<typeof useListPickupLocationsQuery>[0];

/** The list query, with the status facet on the first page (`withStatusFacetOnFirstPage`). */
const usePagedPickupLocationsQuery = (arg: Record<string, unknown>, options?: { skip?: boolean }) =>
  useListPickupLocationsQuery(withStatusFacetOnFirstPage(arg) as ListArg, options);

/** Serializes a request for the page hook's reset key; the ids are bigints. */
const requestKey = (request: object | null) =>
  JSON.stringify(request, (_key, value: unknown) =>
    typeof value === "bigint" ? String(value) : value,
  );

/**
 * The pickup locations for the open status tab, a count for every tab, and the route
 * groups and trucks the cascade joins onto them.
 *
 * Normally pages of 100 load as the table scrolls, with every filter and the column sort
 * applied on the server, so each page is cut from the order of the whole match; the
 * first page carries the status facet the tabs are counted from. While a filter the
 * server cannot apply is set (`needsLocalFiltering`), the whole match for the other
 * filters is fetched once and narrowed here, both statuses at once, so the tabs are
 * counted from that same set — and the table orders it by the column itself
 * (`sortsLocally`).
 *
 * The schedule rows of the loaded locations answer the "has a schedule" column.
 *
 * Route groups and trucks are listed by exact owner, while the locations of a parent
 * organization include its child organizations' — so they are asked for the company and
 * its child organizations up front, plus any other provider among the loaded locations
 * (where the child organizations are not known) and `extraProviderIds`.
 */
export const usePickupLocationList = ({
  providerId,
  criteria,
  status,
  extraProviderIds,
  extraLocation,
  sorting,
}: {
  providerId: number | null | undefined;
  criteria: LocationCriteria;
  status: ConnectProto.LocationStatus;
  /** A location shown beyond the loaded rows (a deep-linked one): its schedules load too. */
  extraLocation?: ConnectProto.PickupLocation;
  /** Providers to load route groups for beyond the loaded rows' (a deep-linked location's). */
  extraProviderIds: string[];
  /** The column sort: sent with every page, or applied here to the whole match. */
  sorting: SortingState;
}) => {
  const isLocal = needsLocalFiltering(criteria);

  const request = useMemo(
    () => (providerId ? locationListRequest(providerId, criteria) : null),
    [providerId, criteria],
  );
  const statusRequest = useMemo(
    () => (request ? { ...request, statuses: [status] } : null),
    [request, status],
  );
  // Every page carries the sort, and a new sort starts again from page 0.
  const pagedRequest = useMemo(() => {
    const sortFilter = toListSort(sorting, LOCATION_SORT_FIELDS);
    return statusRequest && sortFilter
      ? { ...statusRequest, listFilter: sortFilter }
      : statusRequest;
  }, [statusRequest, sorting]);

  const paged = useAccumulatedList<ConnectProto.PickupLocationList, ConnectProto.PickupLocation>({
    useListQuery: usePagedPickupLocationsQuery,
    listEndpoint: pickupLocationApi.endpoints.listPickupLocations,
    arg: pagedRequest ?? {},
    pageSize: PAGE_SIZE,
    filterKey: requestKey(pagedRequest),
    selectRows,
    selectTotal,
    selectRowKey,
    skip: !pagedRequest || isLocal,
  });

  // The tab badges, off the first page's status facet: the same arguments the page
  // hook sends for page 0, so this reads its cache entry rather than asking again.
  // `data` keeps the previous counts while a new filter loads, so the badges do not
  // blink out.
  const { data: firstPage } = useListPickupLocationsQuery(
    withStatusFacetOnFirstPage(withPage(pagedRequest ?? {}, 0, PAGE_SIZE)) as ListArg,
    { skip: !pagedRequest || isLocal },
  );
  const serverCounts = firstPage?.listInfo?.facets?.status?.counts;

  // `currentData` is this request's answer alone; `data` would hand back the previous
  // request's match while a new one loads, narrowed by the new filters.
  const {
    data: localData,
    currentData: currentLocal,
    isFetching: isFetchingLocal,
  } = useListPickupLocationsQuery(request ?? {}, { skip: !request || !isLocal });

  // Known up front, the child organizations make the owners stable: loaded rows add no new
  // provider, so the filter options do not depend on scrolling, and no page re-downloads
  // every group and truck. The rows still add providers when they are not known.
  const { children: childOrgs, isLoading: childOrgsLoading } = useActiveChildOrgs();
  const ownersKey = useMemo(
    () =>
      providersKey(
        [
          ...(providerId ? [String(providerId)] : []),
          ...childOrgs.map((org) => String(org.id)),
          ...extraProviderIds,
        ],
        isLocal ? (localData?.pickupLocations ?? NO_LOCATIONS) : paged.rows,
      ),
    [providerId, childOrgs, extraProviderIds, isLocal, localData, paged.rows],
  );
  const routeOwnerIds = useMemo(
    () => ownersKey.split(",").filter(Boolean).map(BigInt),
    [ownersKey],
  );
  const routeListSkip = !providerId || childOrgsLoading;
  const {
    data: groupsData,
    isFetching: isFetchingGroups,
    isError: groupsFailed,
  } = useListPickupLocationGroupsQuery(
    { ownerOrganizationIds: routeOwnerIds },
    { skip: routeListSkip },
  );
  const {
    data: trucksData,
    isFetching: isFetchingTrucks,
    isError: trucksFailed,
  } = useListTrucksQuery({ ownerOrganizationIds: routeOwnerIds }, { skip: routeListSkip });
  // Loaded once, or failed (the global toaster reports it): never waited on forever.
  const routeIndexLoaded = (!!groupsData || groupsFailed) && (!!trucksData || trucksFailed);
  // The deep link judges the route filters against the index, so it waits for the current
  // one; the list keeps rendering off the previous index while a newer one loads.
  const routeIndexReady = routeIndexLoaded && !isFetchingGroups && !isFetchingTrucks;
  const routeGroups = useMemo(() => groupsData?.pickupLocationGroups ?? [], [groupsData]);
  const trucks = useMemo(() => trucksData?.trucks ?? [], [trucksData]);
  const loadedLocations = isLocal ? (localData?.pickupLocations ?? NO_LOCATIONS) : paged.rows;
  const scheduleLocations = useMemo(
    () => (extraLocation ? [...loadedLocations, extraLocation] : loadedLocations),
    [loadedLocations, extraLocation],
  );
  const { schedules, unknownCgIds } = useLocationSchedules(scheduleLocations);
  // The filters read only route groups and trucks: arriving schedule chunks must not re-run them.
  const routesOnlyIndex = useMemo(
    () => buildCgRouteIndex(routeGroups, trucks),
    [routeGroups, trucks],
  );
  const routeIndex = useMemo(
    () => buildCgRouteIndex(routeGroups, trucks, schedules, unknownCgIds),
    [routeGroups, trucks, schedules, unknownCgIds],
  );

  // Truck and "without route group" read the route index: before its first load they
  // would show an empty list, or every location, so the list waits for it instead.
  const waitsForRouteIndex =
    isLocal && Boolean(criteria.truckId || criteria.withoutRouteGroup) && !routeIndexLoaded;

  // Undefined until this request's match is in, so neither rows nor counts are stale.
  const localMatched = useMemo(
    () =>
      !currentLocal || waitsForRouteIndex
        ? undefined
        : newestFirst(currentLocal.pickupLocations ?? []).filter((location) =>
            matchesLocalCriteria(location, criteria, routesOnlyIndex),
          ),
    [currentLocal, waitsForRouteIndex, criteria, routesOnlyIndex],
  );
  const localRows = useMemo(
    () => (localMatched ?? []).filter((location) => Number(location.status) === status),
    [localMatched, status],
  );

  const counts = useMemo(
    () =>
      isLocal
        ? locationTabCounts({ mode: "local", matched: localMatched })
        : locationTabCounts({ mode: "server", facet: serverCounts }),
    [isLocal, localMatched, serverCounts],
  );

  // A page on screen, the whole match in the file: re-run the request without
  // list_filter at click time. The whole match may hold providers no loaded page has
  // shown yet, whose route groups and trucks are fetched along with it, and locations
  // whose schedule rows are not loaded yet.
  const [fetchAll] = useLazyListPickupLocationsQuery();
  const [fetchGroups] = useLazyListPickupLocationGroupsQuery();
  const [fetchTrucks] = useLazyListTrucksQuery();
  const [fetchSchedules] = useLazyListContainerGroupSchedulesQuery();
  const getExportRows = useCallback(async () => {
    if (!statusRequest) return [];
    const pickupLocations = newestFirst(
      (await fetchAll(statusRequest, true).unwrap()).pickupLocations ?? [],
    );
    const exportOwnersKey = providersKey(ownersKey.split(",").filter(Boolean), pickupLocations);
    const ownerOrganizationIds =
      exportOwnersKey === ownersKey ? null : exportOwnersKey.split(",").map(BigInt);
    const [exportGroups, exportTrucks, exportSchedules] = await Promise.all([
      ownerOrganizationIds
        ? fetchGroups({ ownerOrganizationIds }, true)
            .unwrap()
            .then((data) => data.pickupLocationGroups ?? [])
        : routeGroups,
      ownerOrganizationIds
        ? fetchTrucks({ ownerOrganizationIds }, true)
            .unwrap()
            .then((data) => data.trucks ?? [])
        : trucks,
      fetchLocationSchedules((arg) => fetchSchedules(arg, true).unwrap(), pickupLocations),
    ]);
    return flattenLocationsForExport(
      pickupLocations,
      buildCgRouteIndex(
        exportGroups,
        exportTrucks,
        exportSchedules.schedules,
        exportSchedules.unknownCgIds,
      ),
    );
  }, [
    statusRequest,
    fetchAll,
    fetchGroups,
    fetchTrucks,
    fetchSchedules,
    ownersKey,
    routeGroups,
    trucks,
  ]);

  return {
    locations: isLocal ? localRows : paged.rows,
    /** The whole match is here, newest first: the table orders the locations itself. */
    sortsLocally: isLocal,
    counts,
    routeIndex,
    routesOnlyIndex,
    routeIndexReady,
    routeGroups,
    trucks,
    loading: isLocal
      ? (isFetchingLocal && !currentLocal) || waitsForRouteIndex
      : paged.isFetching && paged.rows.length === 0,
    loadingMore: !isLocal && paged.isFetching && paged.rows.length > 0,
    loadNextPage: !isLocal && paged.hasMore ? paged.loadNextPage : undefined,
    getExportRows: isLocal ? undefined : getExportRows,
  };
};
